import { nothing } from 'lit-html'
import { html, property, state } from 'lit-element'
import navigationStyles from './navigation-rail.styles.scss'
import { navigateToUrl } from '../../../common'
import type { I18NEvents } from '../../../services/i18n'
import { I18NNamespace } from '../../../services/i18n'
import { getEventBus } from '../../../services/namespaces/event-bus'
import { getTranslation } from '../../../services/i18n/i18nUtils'
import { getLaunchDarklyFlag } from '../../../services/feature-flags'
import type { UserProfileEntitlement } from '../../../services/identity/models'
import type { GoToNavigationElementProps, InternalNavigationLink } from '../types'
import { LaunchDarklyVariations } from '../../../services/feature-flags/models'
import { linkIsActive } from '../navigation-item/helpers'
import { getShellApiInstance } from '../../../common/shell-api-helpers'
import { ShellElement } from '../../../common/shell-element'
import { getResolvedIcon } from '../../../common/icon-helpers'
import { ifDefined } from 'lit-html/directives/if-defined'
import type { BadgeNumberEvent } from '../../../services/badge-numbers'
import { setStoreCurrentRoute } from '../../../common/store-current-route'
import type { ShellUserInfo } from '../../../core/models'
import { getEntitlementsFromUserProfile } from '../../../services/identity/userProfile'
import type { shellEvents } from '../../../services/shell-namespace'
import { ShellNamespace } from '../../../services/shell-namespace'
import { unsafeSVG } from 'lit-html/directives/unsafe-svg'

export type State = 'expanded' | 'collapsed'

const TOGGLE_NAVRAIL_EVENT = 'navrail-toggle'
const EXPANDED_NAVRAIL_STATE: State = 'expanded'
const COLLAPSED_NAVRAIL_STATE: State = 'collapsed'

export class GoToNavRail extends ShellElement implements GoToNavigationElementProps {
  static readonly tagName = 'goto-nav-rail'
  private _links: GoToNavigationElementProps['links'] = []

  @state() private visibleLinks: InternalNavigationLink[] = []
  @state() private state: State = EXPANDED_NAVRAIL_STATE

  @property({ type: Array, attribute: 'links', reflect: true })
  get links() {
    return this._links
  }

  set links(values) {
    const oldVal = this._links
    this._links = values.map((link: GoToNavigationElementProps['links'][number]) => ({
      ...link,
      name: getTranslation(link.name),
    }))
    this.requestUpdate('links', oldVal)
  }

  static get styles() {
    return navigationStyles
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    super.attributeChangedCallback(name, oldValue, newValue)
    if (name === 'links' && newValue) {
      this.setVisibleLinks()
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.subscribeLocaleChanges()
    this.subscribeToRouteChanged()
    this.subscribeToNavrailToggle()
  }

  private subscribeToRouteChanged() {
    const { routeChanged } = getEventBus().subscribeTo<typeof ShellNamespace, typeof shellEvents>(ShellNamespace)
    routeChanged.on(this.rerenderNavrail)
    this.unsubscribeFunctions.push(() => routeChanged.removeListener(this.rerenderNavrail))
  }

  private async subscribeToNavrailToggle(): Promise<void> {
    const userPreferencesService = getShellApiInstance().userPreferences
    this.state = (await userPreferencesService?.getPreference(
      `shell.${GoToNavRail.tagName}.state`,
      EXPANDED_NAVRAIL_STATE,
    )) as State

    const listener = () => {
      this.state = this.toggleState(this.state)
      userPreferencesService?.setPreference('shell', { [GoToNavRail.tagName]: { state: this.state } })
      this.requestUpdate()
    }
    this.addEventListener(TOGGLE_NAVRAIL_EVENT, listener)
    this.unsubscribeFunctions.push(() => {
      this.removeEventListener(TOGGLE_NAVRAIL_EVENT, listener)
    })
  }

  private toggleState(currentState: State): State {
    return currentState === EXPANDED_NAVRAIL_STATE ? COLLAPSED_NAVRAIL_STATE : EXPANDED_NAVRAIL_STATE
  }

  private readonly rerenderNavrail = () => {
    this.requestUpdate()
  }

  async setVisibleLinks() {
    const navItems = [...this.links]
    const user = getShellApiInstance().user

    const resolvedVisibilityLinks = (await this.resolveLinksVisibility(navItems, user)).filter(
      (link: InternalNavigationLink) => link._isVisible,
    )

    this.visibleLinks = resolvedVisibilityLinks
    this.subscribeToBadgeEvent()
  }

  /**
   * Resolve links visibility based on profile info
   */
  async resolveLinksVisibility(
    links: InternalNavigationLink[],
    user: ShellUserInfo,
  ): Promise<InternalNavigationLink[]> {
    const isUnresolved = (link: InternalNavigationLink) => link._isVisible === undefined
    const unresolvedLinks = links
      .filter(isUnresolved)
      .map((link: InternalNavigationLink) =>
        link.isVisible?.(
          getEntitlementsFromUserProfile(getShellApiInstance().user) as UserProfileEntitlement[],
          user.emails,
        ),
      )

    for (const isVisible of await Promise.all(unresolvedLinks)) {
      const index = links.findIndex(isUnresolved)
      if (index >= 0) {
        links[index] = Object.assign({}, { ...links[index], _isVisible: isVisible }) as InternalNavigationLink
      }
    }

    return links
  }

  private subscribeLocaleChanges(): void {
    const { localeChanged } = getEventBus().subscribeTo<typeof I18NNamespace, typeof I18NEvents>(I18NNamespace)
    localeChanged.on(this.rerenderNavrail)
    this.unsubscribeFunctions.push(() => localeChanged.removeListener(this.rerenderNavrail))
  }

  private subscribeToBadgeEvent(): void {
    const badgeNumbers = getShellApiInstance().badgeNumbers
    this.visibleLinks.map(link => {
      const listener = (event: BadgeNumberEvent) => {
        this.handleNavigationBadgeEvent(event.value, link.id)
      }
      badgeNumbers.addEventListener('change', listener, { id: link.id, showInNavigation: true })
      this.unsubscribeFunctions.push(() => {
        badgeNumbers.removeEventListener('change', listener)
      })
    })
  }

  private readonly handleNavigationBadgeEvent = (value: number, id: string | undefined): void => {
    const linksWithUpdatedBadgeValues = this.visibleLinks.map(link =>
      id === link.id ? { ...link, badgeCount: value } : link,
    )
    this.visibleLinks = linksWithUpdatedBadgeValues
  }

  render() {
    return html`
      <chameleon-navigation-rail
        class="nav-rail"
        size="medium"
        collapse-label="${getTranslation('Collapse')}"
        state="${this.state}"
      >
        ${this.renderNavItems()} ${this.renderAppSwitcher()}</chameleon-navigation-rail
      >
    `
  }

  private renderNavItems() {
    return this.visibleLinks.length > 0
      ? this.visibleLinks.map(navItem =>
          navItem.externalLink ? this.renderNavRailLink(navItem) : this.renderNavRailItem(navItem),
        )
      : nothing
  }

  private renderNavRailLink(navItem: InternalNavigationLink) {
    const active = linkIsActive(navItem.href)

    return html`
      <chameleon-navigation-rail-link
        href="${navItem.href}"
        data-test="${ifDefined(navItem.dataTest)}"
        ?selected=${active}
      >
        ${navItem.name}
        <chameleon-svg slot="start"
          >${navItem.icon instanceof Function
            ? unsafeSVG(navItem.icon(active))
            : unsafeSVG(getResolvedIcon(navItem.icon, { active }))}</chameleon-svg
        >
        ${this.renderBadge(navItem)}
      </chameleon-navigation-rail-link>
    `
  }

  private renderNavRailItem(navItem: InternalNavigationLink) {
    const active = linkIsActive(navItem.href)

    return html`
      <chameleon-navigation-rail-item
        @change=${() => {
          if (!active) {
            setStoreCurrentRoute(navItem.href)
            navigateToUrl(navItem.href)
          }
        }}
        data-test="${ifDefined(navItem.dataTest)}"
        ?selected=${active}
      >
        ${navItem.name}
        <chameleon-svg slot="start"
          >${navItem.icon instanceof Function
            ? unsafeSVG(navItem.icon(active))
            : unsafeSVG(getResolvedIcon(navItem.icon, { active }))}</chameleon-svg
        >
        ${this.renderBadge(navItem)}
      </chameleon-navigation-rail-item>
    `
  }

  private renderBadge(navItem: InternalNavigationLink) {
    return navItem.badgeCount && navItem.badgeCount > 0
      ? html`
          <chameleon-badge-counter slot="end" data-test="badge-${ifDefined(navItem.dataTest)}">
            ${navItem.badgeCount}</chameleon-badge-counter
          >
        `
      : nothing
  }

  private renderAppSwitcher() {
    return getLaunchDarklyFlag(LaunchDarklyVariations.SHELL_APPLICATION_SWITCHER)
      ? html`
          <goto-app-switcher
            class="chameleon-custom-navigation-rail-item"
            role="menuitem"
            slot="secondary-items"
          ></goto-app-switcher>
        `
      : nothing
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToNavRail.tagName]: GoToNavRail
  }
}
